'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: string;
  badge?: number;
  badgeColor?: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    label: 'Principal',
    items: [
      { id: 'nav-dashboard', label: 'Dashboard', href: '/dashboard', icon: 'HomeIcon' },
      { id: 'nav-create', label: 'Criar Jogo', href: '/create-game', icon: 'SparklesIcon' },
      { id: 'nav-explore', label: 'Explorar', href: '/explore', icon: 'GlobeAltIcon' },
    ],
  },
  {
    label: 'Os Meus Jogos',
    items: [
      { id: 'nav-mygames', label: 'Todos os Jogos', href: '/dashboard', icon: 'RectangleStackIcon' },
      { id: 'nav-published', label: 'Publicados', href: '/dashboard', icon: 'RocketLaunchIcon' },
      { id: 'nav-drafts', label: 'Rascunhos', href: '/dashboard', icon: 'DocumentTextIcon' },
    ],
  },
  {
    label: 'Conta',
    items: [
      { id: 'nav-credits', label: 'Créditos', href: '/credits', icon: 'BoltIcon', badgeColor: 'warning' },
      { id: 'nav-notifications', label: 'Notificações', href: '/dashboard', icon: 'BellIcon', badge: 0, badgeColor: 'primary' },
      { id: 'nav-settings', label: 'Definições', href: '/dashboard', icon: 'Cog6ToothIcon' },
    ],
  },
];

interface SidebarProps {
  currentPath?: string;
}

export default function Sidebar({ currentPath }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { profile, credits, signOut } = useAuth();
  const router = useRouter();

  const isActive = (href: string) => {
    if (!currentPath) return false;
    return currentPath === href || currentPath.startsWith(href + '/');
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      router.push('/sign-up-login-screen');
      router.refresh();
    } catch {}
  };

  const displayName = profile?.display_name || profile?.username || 'Utilizador';
  const initials = displayName.slice(0, 2).toUpperCase();
  const dailyRemaining = (credits?.daily_credits_total ?? 75) - (credits?.daily_credits_used ?? 0);
  const dailyTotal = credits?.daily_credits_total ?? 75;
  const dailyPercent = Math.round((dailyRemaining / dailyTotal) * 100);
  const purchasedCredits = credits?.purchased_credits ?? 0;
  const profileHref = profile?.username ? `/profile/${profile.username}` : '/dashboard';

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`flex items-center h-16 px-4 border-b border-border flex-shrink-0 ${collapsed ? 'justify-center' : 'gap-3'}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-bold text-lg gradient-primary-text tracking-tight">Core Forges</span>
        )}
      </div>

      {/* Credit status mini */}
      {!collapsed && (
        <div className="mx-3 mt-3 p-3 rounded-lg bg-primary/10 border border-primary/20">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-muted-foreground">Créditos Diários</span>
            <Icon name="BoltIcon" size={12} className="text-warning" variant="solid" />
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary to-accent rounded-full transition-all duration-500"
                style={{ width: `${dailyPercent}%` }}
              />
            </div>
            <span className="text-xs font-mono font-semibold text-foreground tabular-nums">{dailyRemaining}/{dailyTotal}</span>
          </div>
          {purchasedCredits > 0 && (
            <p className="text-xs text-muted-foreground mt-1">{purchasedCredits.toLocaleString('pt-PT')} comprados</p>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {navGroups.map((group) => (
          <div key={`group-${group.label}`} className="mb-4">
            {!collapsed && (
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground px-3 mb-2">
                {group.label}
              </p>
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const active = isActive(item.href);
                return (
                  <li key={item.id}>
                    <Link
                      href={item.href}
                      className={`
                        flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                        transition-all duration-150 group relative
                        ${active
                          ? 'bg-primary/15 text-primary border border-primary/20' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                        }
                        ${collapsed ? 'justify-center' : ''}
                      `}
                      title={collapsed ? item.label : undefined}
                    >
                      <Icon
                        name={item.icon as Parameters<typeof Icon>[0]['name']}
                        size={18}
                        className={active ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}
                      />
                      {!collapsed && (
                        <>
                          <span className="flex-1">{item.label}</span>
                          {item.badge !== undefined && item.badge > 0 && (
                            <span className={`
                              text-xs font-semibold px-1.5 py-0.5 rounded-full tabular-nums
                              ${item.badgeColor === 'primary' ? 'bg-primary/20 text-primary' :
                                item.badgeColor === 'warning' ? 'bg-warning/20 text-warning' : 'bg-muted text-muted-foreground'}
                            `}>
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                      {collapsed && item.badge !== undefined && item.badge > 0 && (
                        <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-primary" />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Bottom: User + Collapse */}
      <div className="border-t border-border p-3 space-y-2 flex-shrink-0">
        {!collapsed && (
          <Link
            href={profileHref}
            className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
          >
            <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-white">{initials}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">{displayName}</p>
              <p className="text-xs text-muted-foreground truncate">{purchasedCredits > 0 ? `${purchasedCredits} créditos comprados` : 'Conta gratuita'}</p>
            </div>
            <Icon name="ChevronUpDownIcon" size={14} className="text-muted-foreground flex-shrink-0" />
          </Link>
        )}
        {!collapsed && (
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-danger hover:bg-danger/10 transition-all duration-150"
          >
            <Icon name="ArrowRightOnRectangleIcon" size={16} />
            <span className="text-xs">Sair</span>
          </button>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-150"
        >
          <Icon name={collapsed ? 'ChevronDoubleRightIcon' : 'ChevronDoubleLeftIcon'} size={16} />
          {!collapsed && <span className="text-xs">Recolher</span>}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={`
          hidden lg:flex flex-col flex-shrink-0 border-r border-border bg-card
          transition-all duration-300 ease-in-out
          ${collapsed ? 'w-16' : 'w-60'}
        `}
      >
        <SidebarContent />
      </aside>

      {/* Mobile toggle button */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-card border border-border shadow-lg"
      >
        <Icon name="Bars3Icon" size={20} className="text-foreground" />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <aside
        className={`
          lg:hidden fixed top-0 left-0 z-50 h-full w-72 bg-card border-r border-border
          transition-transform duration-300 ease-in-out
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="absolute top-4 right-4">
          <button
            onClick={() => setMobileOpen(false)}
            className="p-2 rounded-lg hover:bg-muted/50 transition-colors"
          >
            <Icon name="XMarkIcon" size={18} className="text-muted-foreground" />
          </button>
        </div>
        <SidebarContent />
      </aside>
    </>
  );
}